import { forwardRef, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQueryClient } from 'react-query';
import { Virtuoso } from 'react-virtuoso';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, ChatFill, LikeFill } from '@peacecover/icon';
import { useSceneScrollManager } from './hooks/useSceneScrollManager';
import { getContentKey, useSceneChatList } from './hooks/useScenePaginationList';
import {
  SCENE_READY_POLL_INTERVAL_MS,
  useGetSceneDetail
} from '../../domains/scene/ui/hooks/useGetSceneDetail';
import { useLikeAdaptedFictionEdition } from '../../domains/scene/ui/hooks/useLikeAdaptedFictionEdition';
import { useGetBettieBalances } from '../../domains/bettie/ui/hooks/useGetBettieBalances';
import { usePaymentSuccessMessage } from '../hooks/usePaymentSuccessMessage';
import { useHandleHistoryBack } from '../../utils/useHandleHistoryBack';
import { getCastRelationshipId } from './utils/getCastRelationshipId';
import { getChatDirection } from './utils/getChatDirection';
import { getSceneTags } from './utils/getSceneTags';
import ThreadContentItem from './components/ThreadContentItem';
import ChatContentItem from './components/ChatContentItem';
import PreviewHeaderItem from './components/PreviewHeaderItem';
import PreviewCastRelationship from './components/PreviewCastRelationship';
import CommentBottomSheet from './components/CommentBottomSheet';
import SceneOriginalProductList from './components/SceneOriginalProductList';
import BettieChargeBottomSheet from '../../components/bottomSheet/bettie/BettieChargeBottomSheet';
import LoadingSceneContentsOverlay from './components/LoadingSceneContentsOverlay';
import SettingThirdPersonaPopup from './components/SettingThirdPersonaPopup';
import MarqueeTitle from '../components/MarqueeTitle';
import OpenNextPreviewCard from './components/OpenNextPreviewCard';
import { ROUTES } from '../../constants/routes';
import { SCENE_QUERY_KEYS } from '../../domains/scene/ui/queryKeys';
import { BETTIE_QUERY_KEYS } from '../../domains/bettie/ui/queryKeys';
import type { components } from '../../types/schema';

type SceneDetail = components['schemas']['ApiV1Scene'];
type SceneContent =
  | components['schemas']['ApiV1SceneChat']
  | components['schemas']['ApiV1SceneThread'];

const STAR_HIDE_TIMEOUT = 2500;
const HEART_COUNT_PER_PREVIEW = 2;

const VirtuosoScroller = forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={`hiddenScrollBar overscroll-none ${className ?? ''}`.trim()}
      {...props}
    />
  )
);
VirtuosoScroller.displayName = 'VirtuosoScroller';

const ScenePreviewDetailPage = () => {
  const { t } = useTranslation();
  const { sceneId } = useParams();
  const queryClient = useQueryClient();
  const [isCommentSheetOpen, setIsCommentSheetOpen] = useState(false);
  const [isSettingThirdPersonaPopupOpen, setIsSettingThirdPersonaPopupOpen] = useState(false);
  const [isBettieSheetOpen, setIsBettieSheetOpen] = useState(false);
  const [showStarForIndicesFrom, setShowStarForIndicesFrom] = useState<number | null>(null);
  const starHideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [refetchInterval, setRefetchInterval] = useState<number | false>(false);
  const { data: sceneDetail, refetch: refetchSceneDetail } = useGetSceneDetail(sceneId ?? '', {
    refetchInterval
  });

  const { data: bettieBalances } = useGetBettieBalances();
  const hasEnoughHearts =
    bettieBalances?.bettiePoint?.balance &&
    bettieBalances.bettiePoint.balance >= HEART_COUNT_PER_PREVIEW;

  useEffect(() => {
    if (sceneDetail) {
      if (sceneDetail.status === 'completed' || sceneDetail.status === 'primary_ready') {
        setRefetchInterval(false);
      } else {
        setRefetchInterval(SCENE_READY_POLL_INTERVAL_MS);
      }
    }
  }, [sceneDetail]);

  useEffect(() => {
    if (sceneDetail?.personCRequired && !sceneDetail?.personCConfigured) {
      setIsSettingThirdPersonaPopupOpen(true);
    }
  }, [sceneDetail]);

  const isSceneContentLoading =
    !!sceneDetail && (sceneDetail.status === 'pending' || sceneDetail.status === 'generating');
  const { mutateAsync: likeAdaptedFictionEdition } = useLikeAdaptedFictionEdition();
  const castRelationshipId = getCastRelationshipId();

  const {
    contents,
    isBeforeMode,
    isLoading,
    isFetchingPreviousPage,
    isFetchingNextOne,
    forwardCursor,
    loadMoreBefore,
    handleOpenNextPreview,
    baseContentsLength,
    remainingPreviewsCount,
    totalCount,
    resetKey
  } = useSceneChatList(sceneId, sceneDetail?.adaptedFictionPreviewId ?? '');

  const hasSequenceOneInList = useMemo(() => {
    return contents.some((content) => content.sequence === 1);
  }, [contents]);

  const { virtuosoRef, hasScrolledRef, markPrependFromBaseLength } = useSceneScrollManager({
    isBeforeMode,
    baseContentsLength,
    isFetchingPreviousPage,
    contentsLength: contents.length
  });

  useEffect(() => {
    void resetKey;
    hasScrolledRef.current = false;
  }, [resetKey, hasScrolledRef]);

  useEffect(() => {
    return () => {
      if (starHideTimeoutRef.current) clearTimeout(starHideTimeoutRef.current);
    };
  }, []);

  usePaymentSuccessMessage({
    onPaymentSuccess: () => {
      queryClient.invalidateQueries(SCENE_QUERY_KEYS.sceneDetail(sceneId));
    }
  });

  useEffect(() => {
    return () => {
      queryClient.invalidateQueries(SCENE_QUERY_KEYS.sceneSubscriptions());
    };
  }, [queryClient]);

  const handleLoadMoreBeforeWithScroll = () => {
    if (!isBeforeMode) return;
    markPrependFromBaseLength();
    hasScrolledRef.current = true;
    loadMoreBefore();
  };

  const getLastContentId = (): string | null => {
    if (contents.length === 0) return null;
    const last = contents[contents.length - 1];
    if ('sceneThreadId' in last && last.sceneThreadId) return last.sceneThreadId;
    if ('sceneChatId' in last && (last as { sceneChatId?: string }).sceneChatId) {
      return (last as { sceneChatId: string }).sceneChatId;
    }
    return null;
  };

  const handleLike = async () => {
    if (!sceneDetail?.adaptedFictionEditionId) return;
    const lastContentId = getLastContentId();
    if (!lastContentId) return;
    try {
      const contentType =
        contents.length > 0 && 'sceneThreadId' in contents[contents.length - 1]
          ? ('SceneThread' as const)
          : ('SceneChat' as const);
      const contexts = [
        { context_type: contentType, context_id: lastContentId },
        { context_type: 'CastRelationship' as const, context_id: castRelationshipId }
      ];
      await likeAdaptedFictionEdition({
        adaptedFictionEditionId: sceneDetail.adaptedFictionEditionId,
        body: { engagement_contexts: contexts }
      });
      await refetchSceneDetail();
    } catch {
      // 좋아요 실패 시 무시
    }
  };

  const handleOpenNextPreviewWithHeartCheck = async () => {
    if (!hasEnoughHearts) {
      setIsBettieSheetOpen(true);
      return;
    }
    const startIndex = contents.length;
    try {
      await handleOpenNextPreview();
      queryClient.invalidateQueries(BETTIE_QUERY_KEYS.bettieBalances);
      queryClient.invalidateQueries(SCENE_QUERY_KEYS.adaptedFictionPreviews);
      if (starHideTimeoutRef.current) clearTimeout(starHideTimeoutRef.current);
      setShowStarForIndicesFrom(startIndex);
      starHideTimeoutRef.current = setTimeout(() => {
        setShowStarForIndicesFrom(null);
        starHideTimeoutRef.current = null;
      }, STAR_HIDE_TIMEOUT);
    } catch {
      console.error('Failed to open next preview');
    }
  };

  return (
    <div className="flex min-h-screen flex-col overflow-hidden overscroll-none pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)] bg-background-dark">
      {isSceneContentLoading && <LoadingSceneContentsOverlay />}
      <Helmet>
        <title>{sceneDetail?.title ?? t('ScenePreviewDetail.pageTitle')}</title>
      </Helmet>
      <Header sceneDetail={sceneDetail ?? null} />
      <main className="relative flex flex-1 flex-col min-h-0 bg-[#000] mt-[95px]">
        {isLoading || contents.length === 0 ? null : (
          <div className="absolute inset-0 min-h-0">
            <Virtuoso
              ref={virtuosoRef}
              overscan={300}
              defaultItemHeight={80}
              style={{ height: '100%' }}
              data={contents}
              startReached={isBeforeMode ? handleLoadMoreBeforeWithScroll : undefined}
              atTopStateChange={
                isBeforeMode ? (atTop) => atTop && handleLoadMoreBeforeWithScroll() : undefined
              }
              atBottomThreshold={isBeforeMode ? undefined : 200}
              computeItemKey={(_index, content: SceneContent) => getContentKey(content)}
              itemContent={(index, content: SceneContent) => {
                if (sceneDetail?.contentType === 'SceneThread' && 'threadType' in content) {
                  return (
                    <ThreadContentItem
                      speaker={content.speaker ?? undefined}
                      threadType={content.threadType ?? ''}
                      text={content.text}
                      showStar={showStarForIndicesFrom !== null && index >= showStarForIndicesFrom}
                      hasLine={
                        totalCount > 0 ? content.sequence < totalCount : index < contents.length - 1
                      }
                      className={content.threadType === 'passage' ? '!pl-[24px] !pr-[16px]' : ''}
                    />
                  );
                }
                if (sceneDetail?.contentType === 'SceneChat' && 'chatType' in content) {
                  return (
                    <MotionWrapper
                      showMotion={
                        showStarForIndicesFrom !== null && index >= showStarForIndicesFrom
                      }
                    >
                      <ChatContentItem
                        chatType={content.chatType}
                        text={content.text}
                        // @TODO: 이미지 URL 추가
                        pictureUrl={''}
                        direction={getChatDirection(
                          sceneDetail?.castRelationship,
                          content.speaker ?? ''
                        )}
                        showStar={
                          showStarForIndicesFrom !== null && index === showStarForIndicesFrom
                        }
                        className={content.chatType === 'passage' ? '!pl-[24px] !pr-[16px]' : ''}
                      />
                    </MotionWrapper>
                  );
                }
              }}
              components={{
                Scroller: VirtuosoScroller,
                Header: () => (
                  <>
                    {isBeforeMode && isFetchingPreviousPage ? (
                      <div className="px-[22px] pt-[12px]">
                        <BeforeTopLoadingSkeleton />
                      </div>
                    ) : null}
                    {hasSequenceOneInList ? (
                      <PreviewHeaderItem
                        tags={getSceneTags(sceneDetail ?? null)}
                        className="mt-[16px] mb-[16px]"
                      />
                    ) : null}
                  </>
                ),
                Footer: () => (
                  <>
                    {remainingPreviewsCount !== 0 && (
                      <OpenNextPreviewCard
                        remainingPreviewsCount={remainingPreviewsCount}
                        speaker={contents.find((content) => content.speaker)?.speaker ?? null}
                        disabled={!forwardCursor || isFetchingNextOne}
                        onClick={handleOpenNextPreviewWithHeartCheck}
                        isThread={sceneDetail?.contentType === 'SceneThread'}
                      />
                    )}
                    <SceneOriginalProductList
                      sceneId={sceneId ?? ''}
                      sceneDetail={sceneDetail ?? null}
                      castRelationshipId={castRelationshipId}
                      isFullAccessPurchased={sceneDetail?.fullAccessPurchased ?? false}
                    />
                  </>
                )
              }}
            />
          </div>
        )}
      </main>
      <SceneStatusBar
        sceneDetail={sceneDetail ?? null}
        handleLike={handleLike}
        handleComment={() => setIsCommentSheetOpen(true)}
      />
      {sceneDetail?.adaptedFictionEditionId && sceneId && (
        <CommentBottomSheet
          adaptedFictionEditionId={sceneDetail.adaptedFictionEditionId}
          sceneId={sceneId}
          isOpen={isCommentSheetOpen}
          onClose={() => setIsCommentSheetOpen(false)}
          snapToBottom
          isDarkMode={true}
        />
      )}
      {isSettingThirdPersonaPopupOpen && (
        <SettingThirdPersonaPopup
          onClose={() => setIsSettingThirdPersonaPopupOpen(false)}
          castRelationshipId={sceneDetail?.castRelationship?.castRelationshipId ?? ''}
        />
      )}
      <BettieChargeBottomSheet
        isOpen={isBettieSheetOpen}
        onClose={() => setIsBettieSheetOpen(false)}
        onetimeOnly
      />
    </div>
  );
};

export default ScenePreviewDetailPage;

const Header = ({ sceneDetail }: { sceneDetail: SceneDetail | null }) => {
  const { t } = useTranslation();
  const handleHistoryBack = useHandleHistoryBack();
  const navigate = useNavigate();
  return (
    <header className="flex flex-col bg-backgroundElevated-dark fixed top-0 left-0 right-0 z-10 pt-[env(safe-area-inset-top)]">
      <div className="flex items-center justify-between w-full px-[16px] py-[10px] relative">
        <button type="button" onClick={handleHistoryBack}>
          <ArrowLeft size={24} className="text-main-dark-1" />
        </button>
        <MarqueeTitle title={sceneDetail?.title ?? ''} />
      </div>
      <div className="px-[24px] py-[14px] flex items-center justify-between text-pure-white">
        <PreviewCastRelationship castRelationship={sceneDetail?.castRelationship ?? null} />
        <button
          type="button"
          className="flex items-center gap-[24px]"
          onClick={() => navigate(ROUTES.SCENE.CAST_RELATIONSHIPS)}
        >
          <div className="w-[1px] h-[24px] bg-sub-dark-2" />
          <p className="text-[12px] font-medium leading-[14px]">{t('ScenePreviewDetail.change')}</p>
        </button>
      </div>
    </header>
  );
};

const SceneStatusBar = ({
  sceneDetail,
  handleLike,
  handleComment
}: {
  sceneDetail: SceneDetail | null;
  handleLike: () => void;
  handleComment: () => void;
}) => {
  return (
    <div className="fixed bottom-0 left-0 right-0 pb-[calc(env(safe-area-inset-bottom)+10px)] bg-pure-black pt-[16px] rounded-t-[20px] px-[20px] flex items-center justify-start gap-[24px]">
      <button type="button" className="flex flex-col items-center" onClick={handleLike}>
        <LikeFill
          className="text-pure-white"
          gradientStops={
            sceneDetail?.isLiked
              ? [
                  { offset: '0%', color: '#FF45A9' },
                  { offset: '100%', color: '#FF6A6A' }
                ]
              : []
          }
        />
        <p className="text-[11px] font-semibold leading-[13px] text-pure-white">
          {sceneDetail?.likesCount}
        </p>
      </button>
      <button type="button" className="flex flex-col items-center" onClick={handleComment}>
        <ChatFill className="text-pure-white" />
        <p className="text-[11px] font-semibold leading-[13px] text-pure-white">
          {sceneDetail?.commentsCount}
        </p>
      </button>
    </div>
  );
};

const BeforeTopLoadingSkeleton = () => {
  return (
    <output className="flex gap-[10px] items-stretch py-[15px] animate-pulse" aria-live="polite">
      <div className="flex flex-col items-center gap-[8px]">
        <div className="w-[26px] h-[26px] rounded-full bg-white/10 shrink-0" />
        <div className="w-[2px] flex-1 min-h-[40px] rounded-[16px] bg-white/10" />
      </div>
      <div className="flex flex-col gap-[5px] flex-1 min-w-0">
        <div className="h-[17px] w-[120px] rounded bg-white/10" />
        <div className="h-[19px] w-full rounded bg-white/10" />
        <div className="h-[19px] w-[92%] rounded bg-white/10" />
        <div className="h-[19px] w-[78%] rounded bg-white/10" />
      </div>
    </output>
  );
};

const MotionWrapper = ({
  children,
  showMotion
}: {
  children: React.ReactNode;
  showMotion: boolean;
}) => {
  return (
    <motion.div
      initial={showMotion ? { opacity: 0, y: 5 } : false}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, ease: 'easeInOut' }}
    >
      {children}
    </motion.div>
  );
};
